import React, { useState } from "react";
import { X, ShieldCheck, Shield, User, Mail, UserPlus, Edit, CheckCircle, Copy, Check } from "lucide-react";
import { useFormik } from "formik";
import * as Yup from "yup";

// Imports do Firebase Cloud Firestore
import { collection, addDoc, doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../config/firebase"; 

/**
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {function} props.onClose
 * @param {any} [props.memberToEdit]
 */
const InviteTeamModal = ({ isOpen, onClose, memberToEdit }) => {
  const brandColor = "#00a99d";
  const isEditing = !!memberToEdit;
  
  // Estados para o fluxo de "Link Mágico"
  const [inviteLink, setInviteLink] = useState("");
  const [copied, setCopied] = useState(false);

  const formik = useFormik({
    initialValues: { 
      name: memberToEdit?.name || "", 
      email: memberToEdit?.email || "", 
      role: memberToEdit?.role || "operacional",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      name: Yup.string().required("O nome é obrigatório"),
      email: Yup.string()
        .email("Formato de e-mail inválido")
        .required("O e-mail é obrigatório"),
      role: Yup.string().required("Selecione um nível de acesso"),
    }),
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      setSubmitting(true);
      
      try {
        if (isEditing) {
          const memberRef = doc(db, "team", memberToEdit.id);
          await updateDoc(memberRef, {
            name: values.name,
            email: values.email, 
            role: values.role,
          });
          resetForm();
          onClose();
        } else {
          const tokenBytes = new Uint8Array(32);
          window.crypto.getRandomValues(tokenBytes);
          const inviteToken = Array.from(tokenBytes)
            .map((byte) => byte.toString(16).padStart(2, "0"))
            .join("");

          const tokenData = new TextEncoder().encode(inviteToken);
          const tokenDigest = await window.crypto.subtle.digest("SHA-256", tokenData);
          const inviteTokenHash = Array.from(new Uint8Array(tokenDigest))
            .map((byte) => byte.toString(16).padStart(2, "0"))
            .join("");

          const docRef = await addDoc(collection(db, "team"), {
            name: values.name,
            email: values.email.toLowerCase(),
            role: values.role,
            status: "Pendente",
            inviteTokenHash,
            createdAt: serverTimestamp(),
          });

          const baseUrl = window.location.origin;
          const generatedLink =
            `${baseUrl}/setup-interno/${docRef.id}` +
            `?email=${encodeURIComponent(values.email.toLowerCase())}` +
            `&token=${encodeURIComponent(inviteToken)}`;
          
          setInviteLink(generatedLink);
        }
      } catch (error) {
        console.error("Erro ao salvar no Firebase:", error);
        alert(`Erro ao ${isEditing ? 'atualizar' : 'criar usuário'}. Verifique o console.`);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleCopyLink = () => {
    navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCloseAndReset = () => {
    formik.resetForm();
    setInviteLink("");
    setCopied(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto overflow-x-hidden bg-slate-900/60 backdrop-blur-sm animate-fade-in p-4">
      <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
        
        {/* HEADER */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-gray-50/50">
          <div className="flex items-center space-x-3">
            <div className="p-2.5 rounded-xl text-white shadow-sm" style={{ backgroundColor: brandColor }}>
              {isEditing ? <Edit className="w-5 h-5" /> : inviteLink ? <CheckCircle className="w-5 h-5" /> : <UserPlus className="w-5 h-5" />}
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900 leading-tight">
                {isEditing ? 'Editar Colaborador' : inviteLink ? 'Convite Gerado!' : 'Novo Colaborador'}
              </h3>
              <p className="text-xs font-medium text-gray-500 mt-0.5">Gestão de acessos da equipe interna</p>
            </div>
          </div>
          <button
            onClick={handleCloseAndReset}
            className="text-gray-400 bg-white border border-gray-200 shadow-sm hover:bg-gray-50 hover:text-gray-900 rounded-full p-2 transition-all"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* CORPO DO MODAL */}
        <div className="p-6 sm:p-8">
          
          {inviteLink ? (
            <div className="space-y-6 text-center animate-fade-in">
              <div className="w-20 h-20 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-teal-500" />
              </div>
              <div>
                <h4 className="text-lg font-bold text-gray-900">Acesso criado com sucesso!</h4>
                <p className="text-sm text-gray-500 mt-2 max-w-sm mx-auto">
                  Envie o link abaixo para <strong>{formik.values.name}</strong>. Ao clicar, o colaborador poderá definir sua própria senha e acessar o sistema.
                </p>
              </div>

              <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 flex items-center justify-between gap-4 mt-6">
                <code className="text-sm text-gray-700 truncate select-all">{inviteLink}</code>
                <button
                  onClick={handleCopyLink}
                  className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-bold text-sm transition-all shrink-0 ${
                    copied ? 'bg-green-100 text-green-700' : 'bg-brand-primary/10 text-brand-primary hover:bg-brand-primary hover:text-white'
                  }`}
                >
                  {copied ? <><Check className="w-4 h-4"/> Copiado</> : <><Copy className="w-4 h-4"/> Copiar Link</>}
                </button>
              </div>

              <div className="pt-6 border-t border-gray-100">
                <button
                  onClick={handleCloseAndReset}
                  className="px-8 py-2.5 text-sm font-bold text-white rounded-xl shadow-md hover:brightness-110 transition-all w-full sm:w-auto"
                  style={{ backgroundColor: brandColor }}
                >
                  Concluir e Fechar
                </button>
              </div>
            </div>
          ) : (
            
            <form onSubmit={formik.handleSubmit} className="space-y-6 animate-fade-in">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block mb-2 text-xs font-bold text-gray-700 uppercase tracking-wide">
                    Nome Completo
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none"><User className="w-4 h-4 text-gray-400" /></div>
                    <input
                      type="text"
                      {...formik.getFieldProps("name")}
                      className={`bg-white border text-gray-900 text-sm rounded-xl focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 block w-full pl-10 p-2.5 transition-all shadow-sm ${formik.touched.name && formik.errors.name ? 'border-red-500' : 'border-gray-200'}`}
                      placeholder="Ex: Cleberson"
                    />
                  </div>
                  {formik.touched.name && formik.errors.name && <p className="mt-1.5 text-xs font-bold text-red-500">{String(formik.errors.name)}</p>}
                </div>

                <div>
                  <label className="block mb-2 text-xs font-bold text-gray-700 uppercase tracking-wide">
                    E-mail de Acesso
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none"><Mail className="w-4 h-4 text-gray-400" /></div>
                    <input
                      type="email"
                      {...formik.getFieldProps("email")}
                      className={`bg-white border text-gray-900 text-sm rounded-xl focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 block w-full pl-10 p-2.5 transition-all shadow-sm ${formik.touched.email && formik.errors.email ? 'border-red-500' : 'border-gray-200'}`}
                      placeholder="cleberson@realbeneficios.com.br"
                    />
                  </div>
                  {formik.touched.email && formik.errors.email && <p className="mt-1.5 text-xs font-bold text-red-500">{String(formik.errors.email)}</p>}
                </div>
              </div>

              <div>
                <label className="block mb-3 text-xs font-bold text-gray-700 uppercase tracking-wide">
                  Nível de Acesso (Permissões)
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* MASTER - Roxo */}
                  <label className={`relative flex flex-col p-4 cursor-pointer rounded-xl border-2 transition-all shadow-sm ${formik.values.role === 'master' ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50/50 bg-white'}`}>
                    <input type="radio" name="role" value="master" onChange={formik.handleChange} className="sr-only" />
                    <ShieldCheck className={`w-6 h-6 mb-2 transition-colors ${formik.values.role === 'master' ? 'text-purple-600' : 'text-gray-400'}`} />
                    <span className="font-bold text-gray-900 mb-1">Master</span>
                    <span className="text-xs font-medium text-gray-500 leading-snug">Controle total do sistema e equipe.</span>
                  </label>

                  {/* FINANCEIRO - Azul */}
                  <label className={`relative flex flex-col p-4 cursor-pointer rounded-xl border-2 transition-all shadow-sm ${formik.values.role === 'financeiro' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/50 bg-white'}`}>
                    <input type="radio" name="role" value="financeiro" onChange={formik.handleChange} className="sr-only" />
                    <Shield className={`w-6 h-6 mb-2 transition-colors ${formik.values.role === 'financeiro' ? 'text-blue-600' : 'text-gray-400'}`} />
                    <span className="font-bold text-gray-900 mb-1">Financeiro</span>
                    <span className="text-xs font-medium text-gray-500 leading-snug">Visualização de métricas e faturas.</span>
                  </label>

                  {/* OPERACIONAL - Amarelo (Usando yellow nativo do Tailwind) */}
                  <label className={`relative flex flex-col p-4 cursor-pointer rounded-xl border-2 transition-all shadow-sm ${formik.values.role === 'operacional' ? 'border-yellow-500 bg-yellow-50' : 'border-gray-200 hover:border-yellow-300 hover:bg-yellow-50/50 bg-white'}`}>
                    <input type="radio" name="role" value="operacional" onChange={formik.handleChange} className="sr-only" />
                    <User className={`w-6 h-6 mb-2 transition-colors ${formik.values.role === 'operacional' ? 'text-yellow-600' : 'text-gray-400'}`} />
                    <span className="font-bold text-gray-900 mb-1">Operacional</span>
                    <span className="text-xs font-medium text-gray-500 leading-snug">Aprovação de fichas e atendimento.</span>
                  </label>
                </div>
              </div>

              <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-100">
                <button
                  type="button"
                  onClick={handleCloseAndReset}
                  className="px-6 py-2.5 text-sm font-bold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors shadow-sm"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={formik.isSubmitting}
                  className="px-8 py-2.5 text-sm font-bold text-white rounded-xl transition-all shadow-md disabled:opacity-50 hover:brightness-110 flex items-center"
                  style={{ backgroundColor: brandColor }}
                >
                  {formik.isSubmitting ? "Processando..." : (isEditing ? "Salvar Alterações" : "Gerar Convite")}
                </button>
              </div>
            </form>
          )}

        </div>
      </div>
    </div>
  );
};

export default InviteTeamModal;